import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  User,
  Globe,
  Accessibility,
  KeyRound,
  Save,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { toastApiError } from "@/lib/errors";
import {
  isBiggerFontSize,
  isContrastColor,
  setBiggerFontSize,
  setContrastColor,
} from "@/lib/accessibility";
import i18n from "@/i18n";
import { APP_TITLE_SUFFIX } from "@/constants/appMeta";

function Section({ icon: Icon, title, children }) {
  return (
    <div
      className="rounded-lg p-6"
      style={{
        background: "var(--ct-surface)",
        border: "1px solid var(--ct-border)",
        boxShadow: "var(--ct-shadow-sm)",
      }}
    >
      <div className="flex items-center gap-2 mb-5">
        {Icon ? (
          <Icon className="w-5 h-5" style={{ color: "var(--ct-accent)" }} />
        ) : null}
        <h2 className="text-lg font-semibold" style={{ color: "var(--ct-text)" }}>
          {title}
        </h2>
      </div>
      {children}
    </div>
  );
}

export default function MyAccountPage() {
  const { t } = useTranslation();
  const { checkAuth } = useAuth();

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Notification preferences (4 channels: tasks/approvals/chat/system).
  const [prefs, setPrefs] = useState({
    tasks: true,
    approvals: true,
    chat: true,
    system: true,
  });
  const [prefsSaving, setPrefsSaving] = useState(null); // channel key

  const fetchPrefs = useCallback(async () => {
    try {
      const r = await api.get("/users/me/notification-preferences");
      setPrefs(r.data);
    } catch (err) {
      // silent — defaults už jsou nastavené
    }
  }, []);

  const togglePref = async (channel) => {
    if (prefsSaving) return;
    const previous = prefs[channel];
    const next = !previous;
    setPrefs((p) => ({ ...p, [channel]: next }));
    setPrefsSaving(channel);
    try {
      const r = await api.put("/users/me/notification-preferences", { [channel]: next });
      setPrefs(r.data);
    } catch (err) {
      setPrefs((p) => ({ ...p, [channel]: previous }));
      toastApiError(err);
    } finally {
      setPrefsSaving(null);
    }
  };

  // Locales — fetchnuté z BE (parita s portálem). Drží 184 dvojic country+language
  // s display_name. Lab i18n má dnes jen cs+en JSON; ostatní jazyky fallbackují
  // na default v i18next.
  const [locales, setLocales] = useState([]);

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    language: "cs",
    country: "CZ",
    accessibilityColorContrast: false,
    accessibilityFontSize: false,
  });

  const [password, setPassword] = useState({
    oldPassword: "",
    newPassword: "",
    newPasswordConfirm: "",
  });

  useEffect(() => {
    document.title = `${t("my-account.title")} | ${APP_TITLE_SUFFIX}`;
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [meRes, localesRes] = await Promise.all([
          api.get("/users/me"),
          api.get("/users/locales"),
        ]);
        if (cancelled) return;
        const data = meRes.data;
        setLocales(Array.isArray(localesRes.data) ? localesRes.data : []);
        setProfile({
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          username: data.username || "",
          language: data.language || "cs",
          country: data.country || "CZ",
          accessibilityColorContrast:
            data.accessibilityColorContrast ?? isContrastColor(),
          accessibilityFontSize: data.accessibilityFontSize ?? isBiggerFontSize(),
        });
      } catch (err) {
        toastApiError(err, t("my-account.save-error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    // Paralelně načti notification preferences (silent fail).
    fetchPrefs();
    return () => {
      cancelled = true;
    };
  }, [t, fetchPrefs]);

  // Změna země → derivace jazyka ze stejného locale záznamu (parita s portálem).
  const onCountryChange = (countryCode) => {
    const match = locales.find((l) => l.country_code === countryCode);
    setProfile((p) => ({
      ...p,
      country: countryCode,
      language: match?.language_code || p.language,
    }));
  };

  // Aktuálně vybraný locale pro zobrazení v dropdown.
  const sortedLocales = useMemo(
    () =>
      [...locales].sort((a, b) =>
        (a.display_name || "").localeCompare(b.display_name || "", "cs", { sensitivity: "base" }),
      ),
    [locales],
  );

  const submitProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        language: profile.language,
        country: profile.country,
        accessibilityColorContrast: profile.accessibilityColorContrast,
        accessibilityFontSize: profile.accessibilityFontSize,
      };
      await api.patch("/users/me", payload);

      // Aplikuj okamžitě bez čekání na reload — UI se musí změnit hned.
      setContrastColor(profile.accessibilityColorContrast);
      setBiggerFontSize(profile.accessibilityFontSize);
      if (i18n.language !== profile.language) {
        await i18n.changeLanguage(profile.language);
      }

      toast.success(t("my-account.saved"));
      // Refresh auth user so Layout může reagovat na change.
      checkAuth?.();
    } catch (err) {
      toastApiError(err, t("my-account.save-error"));
    } finally {
      setSavingProfile(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (password.newPassword.length < 8) {
      toast.error(t("my-account.password-min-length"));
      return;
    }
    if (password.newPassword !== password.newPasswordConfirm) {
      toast.error(t("my-account.password-mismatch"));
      return;
    }
    setSavingPassword(true);
    try {
      await api.put("/users/me/password", {
        oldPassword: password.oldPassword,
        newPassword: password.newPassword,
      });
      toast.success(t("my-account.password-changed"));
      setPassword({ oldPassword: "", newPassword: "", newPasswordConfirm: "" });
    } catch (err) {
      toastApiError(err, t("my-account.save-error"));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2
          className="w-6 h-6 animate-spin"
          style={{ color: "var(--ct-accent)" }}
        />
        <span className="ml-3 text-sm" style={{ color: "var(--ct-text-secondary)" }}>
          {t("my-account.loading")}
        </span>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: "var(--ct-text)" }}>
        {t("my-account.title")}
      </h1>

      {/* Sekce 1: Osobní údaje */}
      <form onSubmit={submitProfile}>
        <Section icon={User} title={t("my-account.personal-data")}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("my-account.first-name")}</Label>
              <Input
                value={profile.firstName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, firstName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("my-account.last-name")}</Label>
              <Input
                value={profile.lastName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, lastName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("my-account.email")}</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, email: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("my-account.username")}</Label>
              <Input value={profile.username} disabled readOnly />
            </div>
          </div>
        </Section>

        {/* Sekce 2: Prostředí — země (derivuje jazyk) + accessibility (parita s portálem) */}
        <div className="mt-6">
          <Section icon={Globe} title={t("my-account.environment")}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("country.label")}</Label>
                {/* Native select — 184 položek, type-to-filter funguje v prohlížeči. */}
                <select
                  value={profile.country}
                  onChange={(e) => onCountryChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {!sortedLocales.find((l) => l.country_code === profile.country) && (
                    <option value={profile.country}>{profile.country}</option>
                  )}
                  {sortedLocales.map((l) => (
                    <option key={l.country_code} value={l.country_code}>
                      {l.display_name}
                    </option>
                  ))}
                </select>
                <div className="text-xs" style={{ color: "var(--ct-text-secondary)" }}>
                  {t("country.help-language-derived")}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("language.label")}</Label>
                <div
                  className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm"
                  style={{ background: "var(--ct-bg-secondary)", color: "var(--ct-text-secondary)" }}
                >
                  {/* Derivované z country — read-only zobrazení. */}
                  {sortedLocales.find((l) => l.country_code === profile.country)?.language_name
                    || profile.language?.toUpperCase()
                    || "—"}
                </div>
              </div>
            </div>

            <div
              className="mt-5 pt-5 border-t"
              style={{ borderColor: "var(--ct-border)" }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Accessibility
                  className="w-4 h-4"
                  style={{ color: "var(--ct-text-secondary)" }}
                />
                <span
                  className="text-sm font-medium"
                  style={{ color: "var(--ct-text)" }}
                >
                  {t("accessibility.label")}
                </span>
              </div>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={profile.accessibilityColorContrast}
                    onCheckedChange={(v) =>
                      setProfile((p) => ({
                        ...p,
                        accessibilityColorContrast: !!v,
                      }))
                    }
                    className="mt-0.5"
                  />
                  <div>
                    <div
                      className="text-sm"
                      style={{ color: "var(--ct-text)" }}
                    >
                      {t("accessibility.color-contrast")}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "var(--ct-text-secondary)" }}
                    >
                      {t("accessibility.color-contrast-help")}
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={profile.accessibilityFontSize}
                    onCheckedChange={(v) =>
                      setProfile((p) => ({
                        ...p,
                        accessibilityFontSize: !!v,
                      }))
                    }
                    className="mt-0.5"
                  />
                  <div>
                    <div
                      className="text-sm"
                      style={{ color: "var(--ct-text)" }}
                    >
                      {t("accessibility.font-size")}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "var(--ct-text-secondary)" }}
                    >
                      {t("accessibility.font-size-help")}
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </Section>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            disabled={savingProfile}
            style={{ background: "var(--ct-accent)", color: "#fff" }}
          >
            {savingProfile ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {t("common.save")}
          </Button>
        </div>
      </form>

      {/* Sekce 3: Změna hesla */}
      <form onSubmit={submitPassword}>
        <Section icon={KeyRound} title={t("my-account.password")}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>{t("my-account.old-password")}</Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={password.oldPassword}
                onChange={(e) =>
                  setPassword((p) => ({ ...p, oldPassword: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("my-account.new-password")}</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={password.newPassword}
                onChange={(e) =>
                  setPassword((p) => ({ ...p, newPassword: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("my-account.new-password-confirm")}</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={password.newPasswordConfirm}
                onChange={(e) =>
                  setPassword((p) => ({
                    ...p,
                    newPasswordConfirm: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </Section>
        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            disabled={savingPassword}
            variant="outline"
          >
            {savingPassword ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <KeyRound className="w-4 h-4 mr-2" />
            )}
            {t("my-account.change-password")}
          </Button>
        </div>
      </form>

      {/* Sekce 4: Notifikace */}
      <Section icon={Bell} title={t("my-account.notifications-title")}>
        <div className="space-y-3">
          {NOTIFICATION_CHANNELS.map((ch) => (
            <div
              key={ch.key}
              className="flex items-start justify-between gap-4 py-2"
              style={{ opacity: prefsSaving === ch.key ? 0.6 : 1 }}
            >
              <div className="flex-1">
                <div className="text-sm font-medium" style={{ color: "var(--ct-text)" }}>
                  {t(`my-account.notif-${ch.key}-title`)}
                </div>
                <div className="text-xs mt-0.5" style={{ color: "var(--ct-text-secondary)" }}>
                  {t(`my-account.notif-${ch.key}-sub`)}
                </div>
              </div>
              <Switch
                checked={prefs[ch.key]}
                onCheckedChange={() => togglePref(ch.key)}
                disabled={prefsSaving === ch.key}
                aria-label={t(`my-account.notif-${ch.key}-title`)}
              />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

const NOTIFICATION_CHANNELS = [
  { key: "tasks" },
  { key: "approvals" },
  { key: "chat" },
  { key: "system" },
];
